import { useState } from 'react';
import { IonModal } from '@ionic/react';
import { ArrowUp, ArrowDown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { SearchLocalListingsParams } from '../../services/listingService';

// Sort-option keys match the underlying `orderBy` enum values verbatim so
// labels resolve via t(`sort.${key}`). Adding a new sort = add the key
// here + a matching entry in filter.<lang>.json.
type SortKey = NonNullable<SearchLocalListingsParams['orderBy']>;
const SORT_OPTION_KEYS: SortKey[] = ['windowStart', 'distance', 'price', 'quantity'];

// Default direction per key — ascending for time/distance feels natural
// ("soonest first", "nearest first"); descending for price/quantity
// surfaces the high-signal end of the range first.
function defaultDirectionFor(key: SortKey): 'asc' | 'desc' {
  return key === 'windowStart' || key === 'distance' ? 'asc' : 'desc';
}

const DEFAULT_KEY: SortKey = 'windowStart';
const DEFAULT_ORDER: 'asc' | 'desc' = 'asc';

interface HomeFeedSortPillProps {
  orderBy?: SearchLocalListingsParams['orderBy'];
  order?: SearchLocalListingsParams['order'];
  onChange: (next: {
    orderBy: SearchLocalListingsParams['orderBy'];
    order: SearchLocalListingsParams['order'];
  }) => void;
}

export const HomeFeedSortPill: React.FC<HomeFeedSortPillProps> = ({
  orderBy,
  order,
  onChange,
}) => {
  const { t } = useTranslation('filter');
  const [open, setOpen] = useState(false);

  const effectiveKey = (orderBy ?? DEFAULT_KEY) as SortKey;
  const effectiveOrder = (order ?? DEFAULT_ORDER) as 'asc' | 'desc';
  const isDefault = effectiveKey === DEFAULT_KEY && effectiveOrder === DEFAULT_ORDER;

  const handlePick = (key: SortKey) => {
    if (effectiveKey === key) {
      onChange({ orderBy: key, order: effectiveOrder === 'asc' ? 'desc' : 'asc' });
    } else {
      onChange({ orderBy: key, order: defaultDirectionFor(key) });
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('sortBy')}
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border ${
          isDefault
            ? 'border-border text-foreground'
            : 'border-brand-primary bg-brand-primary/10 text-brand-primary'
        }`}
      >
        {effectiveOrder === 'asc' ? (
          <ArrowUp className="h-4 w-4" />
        ) : (
          <ArrowDown className="h-4 w-4" />
        )}
      </button>

      <IonModal
        isOpen={open}
        onDidDismiss={() => setOpen(false)}
        className="auto-sheet"
      >
        {/* Auto-height bottom sheet (see `.auto-sheet` in global.scss): the
            sheet grows to fit these options at any text size instead of being
            pinned to a fixed breakpoint fraction. */}
        <div className="ion-page">
          <div className="auto-sheet-content px-6 pt-4">
            {/* No grab handle: breakpoint-less sheets get no drag gesture from
                Ionic, so a decorative bar just advertises a dismiss that isn't
                wired up. The X here and the Done button at the foot are the
                real ways out; backdrop tap still dismisses too. */}
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold uppercase text-muted-foreground">
                {t('sortBy')}
              </h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t('common:actions.close')}
                className="-mr-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-muted-foreground active:bg-muted"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <ul className="space-y-1">
              {SORT_OPTION_KEYS.map((key) => {
                const active = effectiveKey === key;
                return (
                  <li key={key}>
                    <button
                      type="button"
                      onClick={() => handlePick(key)}
                      className={`flex w-full items-center justify-between rounded-lg px-3 py-3 text-left text-base transition-colors ${
                        active
                          ? 'bg-brand-primary/10 font-medium text-brand-primary'
                          : 'text-foreground'
                      }`}
                    >
                      <span>{t(`sort.${key}`)}</span>
                      {active &&
                        (effectiveOrder === 'asc' ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : (
                          <ArrowDown className="h-4 w-4" />
                        ))}
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* "Done", not "Never mind": picking a sort applies it immediately
                and leaves the sheet open (so you can tap again to flip the
                direction), so there is nothing here to back out of. */}
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="mt-2 w-full py-3 text-center text-base font-medium text-muted-foreground active:opacity-60"
            >
              {t('done')}
            </button>
          </div>
        </div>
      </IonModal>
    </>
  );
};
